import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { getLang } from "@/lib/i18n";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCompanyId, setActiveCompanyId] = useState(
    () => localStorage.getItem("activeCompanyId") || null
  );

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const companies =
    user && user.role === "company"
      ? [{ company_id: user.company_id, company_name: user.company_name, commission: 0 }]
      : user?.companies || [];

  // keep an active company selected
  useEffect(() => {
    if (!user) return;
    const ids = companies.map((c) => c.company_id);
    if (!activeCompanyId || !ids.includes(activeCompanyId)) {
      const next = ids[0] || null;
      setActiveCompanyId(next);
      if (next) localStorage.setItem("activeCompanyId", next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setActiveCompany = (id) => {
    setActiveCompanyId(id);
    localStorage.setItem("activeCompanyId", id);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", { ...payload, lang: getLang() });
    if (!data.needs_verification) setUser(data);
    return data;
  };
  const completeOnboarding = async (payload) => {
    const { data } = await api.post("/auth/complete-onboarding", payload);
    setUser(data);
    return data;
  };
  const refreshUser = checkAuth;
  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed, clearing local session anyway", e);
    }
    setUser(false);
  };

  const activeCompany = companies.find((c) => c.company_id === activeCompanyId) || companies[0] || null;

  return (
    <AuthContext.Provider
      value={{
        user, setUser, loading, login, register, completeOnboarding, logout,
        checkAuth, refreshUser, apiError,
        companies, activeCompanyId, activeCompany, setActiveCompany,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
